import React, { useContext, useEffect, useState } from 'react'

import { Alert, Box, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Switch, TextField, CircularProgress } from '@mui/material'

import useTheme from '../themes/material'
import { AppContext } from '../context/AppContext'
import { ColorModeContext } from '../context/ColorModeContext'
import api from '../lib/api'

export default function DialogUtilisateur ({ user, setUser, onCrudAction }) {
  const app = useContext(AppContext)
  const theme = useTheme()
  const { colorMode, toggleColorMode } = useContext(ColorModeContext)

  const [firstname, setFirstname] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [admin, setAdmin] = useState(false)
  const [active, setActive] = useState(false)
  const [me, setMe] = useState(false)
  const [confirmationDialogDeleteOpen, setConfirmationDialogDeleteOpen] = useState(false)

  const [clientOption, setClientOption] = useState(null)
  const [clientOptions, setClientOptions] = useState([])
  const [clientSearch, setClientSearch] = useState('')
  const [loadingClients, setLoadingClients] = useState(false)

  if (typeof onCrudAction !== 'function') {
    onCrudAction = () => {}
  }

  useEffect(() => {
    setFirstname(user?.firstname || '')
    setName(user?.name || '')
    setEmail(user?.email || '')
    setUsername(user?.username || '')
    setAdmin(!!user?.admin)
    setActive(!!user?.active || !user?.id)
    setMe(user?.id === app.user.id)
    setConfirmationDialogDeleteOpen(false)
    setClientSearch(user?.client_code || '')
    setClientOption(null)
    setClientOptions([])
  }, [user, app.user.id])

  useEffect(() => {
    if (!clientSearch || String(clientSearch).length < 3) {
      // Minimum 3 caractères
      setClientOptions([])
      return
    }

    const controller = new AbortController()

    const timeout = setTimeout(async () => {
      setLoadingClients(true)

      api.get('clients/' + encodeURIComponent(clientSearch), { signal: controller.signal }).then(response => {
        setLoadingClients(false)
        setClientOptions(response.data)
        if (response.data.length === 1 && clientOption === null) {
          setClientOption(response.data[0])
        }
      }).catch(error => {
        setLoadingClients(false)
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          return
        }
        app.snackbarError(error.message || 'Erreur lors du chargement des clients')
      })
    }, 300)

    return () => {
      controller.abort()
      clearTimeout(timeout)
    }
  }, [clientSearch, app])

  const userCreate = () => {
    const createdUser = {
      client_code: clientOption.id || clientSearch,
      firstname,
      name,
      email,
      admin,
      active
    }

    api.post('user', createdUser).then(() => {
      setUser(null)
      onCrudAction()
    }).catch(error => {
      app.snackbarError(error.message || 'Erreur lors de la création de l\'utilisateur')
    })
  }

  const userUpdate = () => {
    const updatedUser = {
      id: user.id,
      client_code: clientOption.id || clientSearch,
      firstname,
      name,
      email,
      admin,
      active
    }

    api.post('user/' + updatedUser.id, updatedUser).then(response => {
      if (me) {
        app.init(response.data)
      }
      setUser(null)
      onCrudAction()
    }).catch(error => {
      app.snackbarError(error.message || 'Erreur lors de la modification de l\'utilisateur')
    })
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    user?.id ? userUpdate() : userCreate()
  }

  const handleDelete = () => {
    if (me) {
      // Impossible de s'auto-supprimer
      return
    }

    api.get('user-delete/' + user.id).then(response => {
      setConfirmationDialogDeleteOpen(false)
      setUser(null)
      onCrudAction()
    }).catch(error => {
      app.snackbarError(error.message || 'Erreur lors de la suppression de l\'utilisateur')
    })
  }

  const sxTextfield = {
    display: 'block',
    margin: theme.spacing(2, 0)
  }

  const renderField = (field, setField, key, label, required, autoFocus) => (
    <TextField
      id={key}
      label={label}
      variant='filled'
      value={field}
      onChange={event => {
        setField(event.target.value)
      }}
      required={required}
      autoFocus={autoFocus}
      fullWidth
      sx={sxTextfield}
    />
  )

  return (
    <Dialog
      open={user !== null}
      onClose={() => setUser(null)}
      fullWidth
      maxWidth='sm'
    >
      <form onSubmit={(event) => handleSubmit(event)}>
        <DialogTitle>Modification de l'utilisateur</DialogTitle>
        <DialogContent>
          <Autocomplete
            disablePortal
            id='client'
            value={clientOption}
            disabled={me && !admin}
            loading={loadingClients}
            options={clientOptions}
            filterOptions={(x) => x} // désactive le filtre local MUI
            noOptionsText={
              String(clientSearch).length < 3
                ? 'Saisissez au moins 3 caractères'
                : 'Aucun résultat'
            }
            onChange={(event, newOption) => {
              setClientOption(newOption)
            }}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') {
                setClientSearch(value || '')
              }
            }}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={(props, option) => (
              <Box
                component="li"
                key={option.id}
                sx={
                  option.client_active ? {} : { color: theme.palette.secondary.main }
                }
                {...props}
              >
                {option.label}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Client'
                variant='filled'
                required={!admin}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingClients && (
                        <CircularProgress color='inherit' size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
          {renderField(firstname, setFirstname, 'name', 'Prénom', true)}
          {renderField(name, setName, 'name', 'Nom', true)}
          {renderField(email, value => {
            setEmail(value)
            if (!value) {
              setUsername('')
            } else if (/^0000-/.test(user?.username)) {
              setUsername('0000-' + value)
            } else {
              setUsername(app.greffe.code + '-' + value)
            }
          }, 'email', 'Email', true)}
          <Alert severity="info">
            <small>Identifiant de connexion{/^0000-/.test(user?.username) ? '' : ' (la saisie du code greffe est facultative)'}</small>
            <Box>{username}</Box>
          </Alert>
          <FormGroup sx={{ margin: theme.spacing(4, 0) }}>
            {!me && app.user.admin && (
              <FormControlLabel
                control={<Switch checked={admin} onChange={() => setAdmin(!admin)} />}
                label='Admin'
              />
            )}
            {!me && app.user.admin && (
              <FormControlLabel
                control={<Switch checked={active} onChange={() => setActive(!active)} />}
                label='Actif'
              />
            )}
            {me && (
              <FormControlLabel
                control={<Switch checked={colorMode === 'dark'} onChange={toggleColorMode} />}
                label='Mode sombre'
              />
            )}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUser(null)}>Annuler</Button>
          <Button type='submit'>Enregistrer</Button>
          {!me && app.user.admin && user?.id && (
            <Button variant='contained' color='secondary' onClick={() => { setConfirmationDialogDeleteOpen(true) }}>
              Supprimer
            </Button>
          )}
        </DialogActions>
      </form>
      <Dialog
        maxWidth='xs'
        open={confirmationDialogDeleteOpen}
      >
        <DialogTitle>Attention</DialogTitle>
        <DialogContent>
          Vous êtes sur le point de supprimer l'utilisateur.
          <br />
          Cette action est irreversible.
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => { setConfirmationDialogDeleteOpen(false) }}>
            Annuler
          </Button>
          <Button onClick={handleDelete}>Supprimer l'utilisateur</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}
